import { Box, Chip, Container, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';

const features = [
  {
    title: '전국 흐름 파악',
    subtitle: '01 · 전국에서 시작하기',
    description: '시장현황으로 전국과 지역별 흐름을 살펴보고, 시장기회에서 관심 있게 관찰할 지역과 아파트를 찾습니다.',
    images: [
      { src: '/assets/mains/market-1.png', label: '시장현황', description: '가격지수, 매물수, 거래 흐름을 한눈에 살펴보며 전국 시장의 방향과 지역별 차이를 확인합니다.' },
      { src: '/assets/mains/market-5.png', label: '시장기회', description: '신고가, 회복과 하락, 가격저평가 등 시장 신호를 비교하며 관심 후보를 찾습니다.' },
    ],
  },
  {
    title: '지역 분석',
    subtitle: '02 · 관심 지역 자세히 보기',
    description: '지역분석의 데이터와 프리셋으로 지역을 비교하고, 지도분석에서 주변 지역까지 흐름이 어떻게 이어지는지 살펴봅니다.',
    images: [
      { src: '/assets/mains/region-2.png', label: '지역분석', description: '가격지수, 매물수, 거래량, 미분양과 입주물량을 선택해 여러 지역을 비교하거나 한 지역을 자세히 분석합니다.' },
      { src: '/assets/mains/region-3.png', label: '지도분석', description: '지도 위에서 지역별 지표와 기간별 변화를 비교하고, 주변 지역과의 차이를 확인합니다.' },
    ],
  },
  {
    title: '아파트 선정',
    subtitle: '03 · 내 기준에 맞는 아파트 찾기',
    description: '아파트검색과 지도검색으로 후보를 찾고, 저평가판단으로 비교 단지와의 가격 차이를 살펴봅니다.',
    images: [
      { src: '/assets/mains/apt-1.png', label: '아파트검색', description: '지역과 년식, 세대수, 평형 등 조건으로 아파트를 찾고 실거래가와 매물시세를 비교합니다.' },
      { src: '/assets/mains/apt-2.png', label: '지도검색', description: '지도에서 아파트의 위치와 주변 단지를 살펴보고, 선택한 단지의 상세정보와 가격 흐름을 확인합니다.' },
      { src: '/assets/mains/apt-3.png', label: '저평가판단', description: '과거 가격 흐름이 비슷했던 단지와 현재 가격을 비교해 상대적으로 저평가된 후보를 검토합니다.' },
    ],
  },
  {
    title: '나만의 분석 노트',
    subtitle: '04 · 기록하고 지속 관찰하기',
    description: '분석만들기로 필요한 지표를 한곳에 모으고, 분석노트에 차트와 생각을 담아 변화를 꾸준히 관찰합니다.',
    images: [
      { src: '/assets/mains/analyze-1.png', label: '분석만들기', description: '관심 지역과 데이터를 선택해 차트를 구성하고, 기준선과 증감·합산 도구로 나만의 분석을 만듭니다.' },
      { src: '/assets/mains/analyze-2.png', label: '분석노트', description: '만든 분석을 노트에 넣고 선정 이유와 관찰할 지표를 기록하며, 이후의 변화와 판단을 다시 확인합니다.' },
    ],
  },
];

/** 기능별 이미지 탭 갤러리 */
const FeatureGallery = ({ images }) => {
  const [selected, setSelected] = useState(0);

  return (
    <Stack spacing={1.5}>
      {/* 메인 이미지 — 컨테이너 크기를 고정해 이미지 비율이 달라도 아래 Chip 위치가 흔들리지 않게 한다. */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 10',
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: (theme) => alpha(theme.palette.common.black, 0.03),
          boxShadow: (theme) =>
            `0 16px 40px ${alpha(theme.palette.common.black, 0.08)}`,
          border: (theme) =>
            `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          },
        }}
      >
        <img
          src={images[selected].src}
          alt={`${images[selected].label} 화면`}
          loading="lazy"
        />
      </Box>
      <Typography color="text.secondary"
        sx={{ fontSize: { xs: 13, md: 14 }, lineHeight: 1.7 }}>
        {images[selected].description}
      </Typography>
      {/* 이미지 선택 탭 */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {images.map((img, idx) => (
          <Chip
            key={img.label}
            label={img.label}
            size="small"
            variant={idx === selected ? 'filled' : 'outlined'}
            color={idx === selected ? 'primary' : 'default'}
            onClick={() => setSelected(idx)}
            aria-pressed={idx === selected}
            sx={{
              fontSize: 12,
              fontWeight: idx === selected ? 700 : 500,
              cursor: 'pointer',
              borderColor: (theme) =>
                idx === selected
                  ? 'primary.main'
                  : alpha(theme.palette.primary.main, 0.3),
            }}
          />
        ))}
      </Box>
    </Stack>
  );
};

export const HomeFeatures = () => {
  return (
    <Box
      id="features"
      sx={{ py: { xs: 10, md: 16 } }}
    >
      <Container maxWidth="lg">
        <Stack
          spacing={2}
          sx={{ mb: { xs: 8, md: 12 }, textAlign: 'center', alignItems: 'center' }}
        >
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 2 }}
          >
            Features
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontWeight: 800, fontSize: { xs: 28, md: 40 } }}
          >
            전국 흐름부터 나만의 분석 노트까지
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ fontSize: { xs: 16, md: 18 }, maxWidth: 600, mx: 'auto' }}
          >
            전국을 보고, 지역을 분석하고, 아파트를 선정합니다. 그 과정을 노트에 담아 지속적으로 관찰하세요.
          </Typography>
        </Stack>

        <Stack spacing={{ xs: 10, md: 16 }}>
          {features.map((feature, index) => {
            const isReversed = index % 2 === 1;

            return (
              <Grid
                key={feature.title}
                container
                spacing={{ xs: 4, md: 6 }}
                alignItems="center"
                direction={isReversed ? 'row-reverse' : 'row'}
              >
                {/* Text */}
                <Grid
                  xs={12}
                  md={4}
                >
                  <Stack spacing={2}>
                    <Typography
                      variant="overline"
                      sx={{ color: 'primary.main', fontWeight: 700 }}
                    >
                      {feature.subtitle}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 800, fontSize: { xs: 24, md: 32 } }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: { xs: 15, md: 16 }, lineHeight: 1.8 }}
                    >
                      {feature.description}
                    </Typography>
                  </Stack>
                </Grid>

                {/* Image Gallery */}
                <Grid
                  xs={12}
                  md={8}
                >
                  <FeatureGallery images={feature.images} />
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};
